import TopNav from "@/components/TopNav";
import Hero from "@/components/hero/Hero";
import ScrollProgress from "@/components/interactive/ScrollProgress";
import Profile from "@/components/sections/Profile";
import Marquee from "@/components/interactive/Marquee";
import Portfolio from "@/components/sections/Portfolio";
import { Blog } from "@/components/sections/Blog";
import { Apps } from "@/components/sections/Apps";
import ContactForm from "@/components/sections/ContactForm";
import { Services } from "@/components/sections/Services";
import { Resume } from "@/components/sections/Resume";
import { FAQSection } from "@/components/sections/FAQSection";
import { useState, useEffect } from "react";
import { SettingsDialog } from "@/components/SettingsDialog";
import { CoachingAnnouncementDialog } from "@/components/CoachingAnnouncementDialog";
import { useInView } from "react-intersection-observer";
import { getSectionFromList, Section } from "@/scripts/section";
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useCallback } from "react";
import type { PortfolioProjectId } from "@/utils/portfolio-highlight";
import { storageGet, storageRemove, storageSet } from "@/utils/safe-storage";
import { useMatchMedia } from "@/hooks/useMatchMedia";
import { DesktopGate } from "@/components/device/DesktopGate";
import type { DeviceSectionId } from "@/components/device/sections";
import { forgetClassic, hasBootedBefore, markGateShown, readGateContext, rememberClassic, shouldOpenGate } from "@/components/device/gate";

const SCROLL_POSITION_KEY = 'homepage-scroll-position';
/** Above the 770px phone breakpoint the home page opens as a device (see road-map/personal-website/DEVICE_OS.md). */
const WIDE_QUERY = '(min-width: 771px)';

export default function HomePage() {
    const { t } = useTranslation();
    const [showMenu, setShowMenu]: [boolean, (value: (((prevState: boolean) => boolean) | boolean)) => void] = useState(false);
    const [highlightedProjectId, setHighlightedProjectId] = useState<PortfolioProjectId | null>(null);
    const clearHighlightedProject = useCallback(() => setHighlightedProjectId(null), []);

    // Decided in the initialiser, before the scroll-restore effect below
    // clears the key that says the visitor is coming back from a subpage.
    const isWide = useMatchMedia(WIDE_QUERY);
    const [gate, setGate] = useState(() => {
        const open = shouldOpenGate(readGateContext(window.matchMedia(WIDE_QUERY).matches));
        return { open, playBoot: open && !hasBootedBefore() };
    });
    const [scrollTarget, setScrollTarget] = useState<DeviceSectionId | null>(null);

    useEffect(() => {
        if (gate.open) markGateShown();
    }, [gate.open]);

    // Narrowed to phone width: the phone hero takes over from the gate.
    useEffect(() => {
        if (!isWide) setGate((current) => (current.open ? { ...current, open: false } : current));
    }, [isWide]);

    const exitGate = useCallback((section: DeviceSectionId | null) => {
        rememberClassic();
        setGate((current) => ({ ...current, open: false }));
        setScrollTarget(section);
    }, []);

    const openGate = useCallback(() => {
        forgetClassic();
        setGate({ open: true, playBoot: true });
    }, []);

    // Runs once the gate has unmounted and released the scroll lock.
    useEffect(() => {
        if (gate.open || !scrollTarget) return;
        document.getElementById(scrollTarget)?.scrollIntoView({ block: 'start' });
        setScrollTarget(null);
    }, [gate.open, scrollTarget]);

    // Restore scroll position on mount
    useEffect(() => {
        const savedPosition = storageGet('session', SCROLL_POSITION_KEY);
        if (savedPosition) {
            // Small delay to ensure DOM is ready
            requestAnimationFrame(() => {
                window.scrollTo(0, parseInt(savedPosition, 10));
            });
            storageRemove('session', SCROLL_POSITION_KEY);
        }
    }, []);

    // Save scroll position before navigating away
    useEffect(() => {
        const handleBeforeUnload = () => {
            storageSet('session', SCROLL_POSITION_KEY, window.scrollY.toString());
        };

        // Save position when user clicks links that navigate away
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const anchor = target.closest('a');
            if (anchor && anchor.href && !anchor.href.includes('#')) {
                storageSet('session', SCROLL_POSITION_KEY, window.scrollY.toString());
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('click', handleClick, true);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleClick, true);
        };
    }, []);

    // InView options - triggers animations when section approaches the viewport
    // threshold: 0.05 triggers when 5% of section is visible
    // rootMargin extends detection area 100px below viewport for earlier triggering
    const inViewOptions = { threshold: 0.05, rootMargin: '0px 0px 100px 0px' };

    const sections: Section[] = [
        {
            title: t('navigation.about'),
            id: "profile",
            viewHook: useInView(inViewOptions)
        },
        {
            title: t('navigation.services'),
            id: "services",
            viewHook: useInView(inViewOptions)
        },
        {
            title: t('navigation.portfolio'),
            id: "portfolio",
            viewHook: useInView(inViewOptions)
        },
        {
            title: t('navigation.apps'),
            id: "apps",
            viewHook: useInView(inViewOptions)
        },
        {
            title: t('navigation.resume'),
            id: "resume",
            viewHook: useInView(inViewOptions)
        },
        {
            title: t('navigation.blog'),
            id: "blog",
            viewHook: useInView(inViewOptions)
        },
        {
            title: t('navigation.faq'),
            id: "faq",
            viewHook: useInView(inViewOptions)
        },
        {
            title: t('navigation.contact'),
            id: "contact",
            viewHook: useInView(inViewOptions)
        },
    ];

    return (
        <>
        <Helmet>
            <title>{t('meta.title', 'Nicholas Y. | Forward-Deployed AI Engineer, Remote')}</title>
            <meta name="description" content="Forward-deployed AI engineer. I ship production software 10x faster with AI agents: smart contracts, full-stack apps." />
            <meta property="og:title" content="Nicholas Y. | Forward-Deployed AI Engineer, Remote" />
            <meta property="og:description" content="Forward-deployed AI engineer. I ship production software 10x faster with AI agents: smart contracts, full-stack apps." />
            <meta property="og:site_name" content="Nicholas Y." />
            <meta property="og:image" content="https://www.0xnicholasy.xyz/og-image.png" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Nicholas Y. - Forward-deployed AI engineer shipping production software with AI agent pipelines" />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://www.0xnicholasy.xyz" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Nicholas Y. | Forward-Deployed AI Engineer, Remote" />
            <meta name="twitter:description" content="Forward-deployed AI engineer. I ship production software 10x faster with AI agents: smart contracts, full-stack apps." />
            <meta name="twitter:image" content="https://www.0xnicholasy.xyz/og-image.png" />
        </Helmet>
        <ScrollProgress />
        <TopNav sections={sections} onOpenSettings={() => setShowMenu(true)} />
        <main className="home-main" onClick={() => setShowMenu(false)}>
            <div className="page-container">
                <Hero onOpenDevice={isWide ? openGate : undefined} />
            </div>
            <Marquee />
            <div className="page-container">
                <Profile section={getSectionFromList(sections, 'profile')} />
                <Services section={getSectionFromList(sections, 'services')} />
                <Portfolio
                    section={getSectionFromList(sections, 'portfolio')}
                    highlightedProjectId={highlightedProjectId}
                    onHighlightComplete={clearHighlightedProject}
                />
                <Apps section={getSectionFromList(sections, 'apps')} />
                <Resume
                    section={getSectionFromList(sections, 'resume')}
                    onFocusProject={setHighlightedProjectId}
                />
                <Blog section={getSectionFromList(sections, 'blog')} />
                <FAQSection section={getSectionFromList(sections, 'faq')} />
                <ContactForm section={getSectionFromList(sections, 'contact')} />
            </div>
        </main>
        <SettingsDialog showMenu={showMenu} setShowMenu={setShowMenu} />
        {/* The announcement waits until the visitor has left the device. */}
        {!gate.open && <CoachingAnnouncementDialog />}
        {gate.open && isWide && <DesktopGate playBoot={gate.playBoot} onExit={exitGate} />}
        </>
    )
}
